/**
 * Role Management Hooks
 *
 * Custom React hooks for managing user roles in the multi-role system
 */

import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "./use-user";
import { useToast } from "./use-toast";
import { authFetch } from "../services/authService";
import type { User } from "../types/user";

type UserRole = "client" | "cleaner" | "admin";

/**
 * Check if the current user has a specific role
 * @param role - The role to check for
 * @returns true if user has the role, false otherwise
 */
export function useHasRole(role: UserRole): boolean {
  const { user } = useUser();

  if (!user) return false;

  // Use roles array
  if (Array.isArray(user.roles)) {
    return user.roles.includes(role);
  }

  // If roles array is missing, default to false (should never happen with current setup)
  return false;
}

/**
 * Check if the current user has ANY of the specified roles
 * @param roles - Array of roles to check for
 * @returns true if user has at least one of the roles
 */
export function useHasAnyRole(roles: UserRole[]): boolean {
  const { user } = useUser();

  if (!user) return false;

  return roles.some(role => {
    if (Array.isArray(user.roles)) {
      return user.roles.includes(role);
    }
    return false;
  });
}

/**
 * Check if the current user has ALL of the specified roles
 * @param roles - Array of roles to check for
 * @returns true if user has all of the roles
 */
export function useHasAllRoles(roles: UserRole[]): boolean {
  const { user } = useUser();

  if (!user) return false;

  return roles.every(role => {
    if (Array.isArray(user.roles)) {
      return user.roles.includes(role);
    }
    return false;
  });
}

/**
 * Get the user's currently active role
 * @returns The active role or null if no user/role
 */
export function useActiveRole(): UserRole | null {
  const { user } = useUser();

  if (!user) return null;

  // If activeRole is set, use that
  if (user.activeRole) {
    return user.activeRole;
  }

  // Fallback: if no activeRole, use the first role in the array
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles[0];
  }

  // If neither activeRole nor roles are set, return null
  return null;
}

/**
 * Get all roles for the current user
 * @returns Array of roles (empty array if no user)
 */
export function useAllRoles(): UserRole[] {
  const { user } = useUser();

  if (!user) return [];

  // Use roles array if available
  if (Array.isArray(user.roles)) {
    return user.roles;
  }

  // If roles array is not set, return empty array
  return [];
}

/**
 * Check if a specific role is the user's currently active role
 * @param role - The role to check
 * @returns true if the role is the user's active role
 */
export function useIsActiveRole(role: UserRole): boolean {
  const activeRole = useActiveRole();
  return activeRole === role;
}

/**
 * Hook for switching between user roles
 * @returns Object with switchRole function and loading/error states
 */
export function useSwitchRole() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async (newRole: UserRole) => {
      const res = await authFetch("/api/auth/switch-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!res.ok) {
        const error = await res.text();
        throw new Error(error || "Failed to switch role");
      }

      return res.json();
    },
    onSuccess: (data: User) => {
      // Update the user data in the cache
      queryClient.setQueryData(["/api/user"], data);

      toast({
        title: "Role switched",
        description: `Now viewing as ${data.activeRole}`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to switch role",
        description: error.message,
      });
    },
  });

  const switchRole = useCallback(
    (role: UserRole) => {
      mutation.mutate(role);
    },
    [mutation]
  );

  return {
    switchRole,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Convenience hooks for specific role checks
 */
export function useIsAdmin(): boolean {
  return useHasRole("admin");
}

export function useIsCleaner(): boolean {
  return useHasRole("cleaner");
}

export function useIsClient(): boolean {
  return useHasRole("client");
}
